import React from 'react';

import { useSelector } from '@datagrid/state';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import type { BackendTypes } from '@tf/api';
import { TFPageContent, TFPageHeader } from '@tf/shared';
import {
	Box,
	Button,
	createModal,
	Flex,
	Group,
	Icon,
	Stack,
	Textarea,
	TFCard,
	TFLoadingOverlay,
	TFNotifier,
	TFText,
	useDisclosure,
	useModalControl,
} from '@tf/ui';
import { M, S, T } from '@tf/utils';

import { useOverviewData } from '@/core/api/overview';
import {
	downloadEvidence,
	useAddPaymentCommentMutation,
	useMakePaymentDecisionMutation,
	usePayment,
	usePaymentCases,
	usePaymentComments,
	usePaymentEvents,
	useResendPaymentDecisionMutation,
	useTransactionAssessmentResults,
} from '@/core/api/payments';
import { appStore } from '@/core/stores';
import type { TransactionAssessment } from '@/core/types/payments';
import { getDisplayName } from '@/core/utils';
import { PaymentCasesList } from '@/components/payments/PaymentCasesList';
import { PaymentDetails } from '@/components/payments/PaymentDetails';
import { PaymentsEventLog } from '@/components/payments/PaymentEventLog';
import { useNestedPaymentForms } from '@/components/payments/useNestedPaymentForms';
import { CommentsList, CounterBadge } from '@/components/shared';

import { getOriginalStatusFromError, shouldShowResendButton } from '../utils';

import { PaymentToolbar } from './PaymentToolbar';
import { LastAssessmentResult, TransactionAssessmentResult } from './TransactionAssessmentResult';

const FINAL_STATUSES = [
	'APPROVED',
	'APPROVED_ERROR',
	'REJECTED_ERROR',
	'REJECTED',
	'ORDER_COMPLETED',
	'ORDER_REJECTED',
	'FAILED',
];

export const PaymentOverview = () => {
	const { paymentId: paymentObjectName } = useParams<{ paymentId: string }>();
	const tenant = useSelector(() => appStore.tenant?.get());

	const decisionModal = useDisclosure();
	const commentsModal = useDisclosure();

	const { data: overviewData } = useOverviewData();
	const listUsers = overviewData?.listUsers ?? [];

	T.assertNotNullish(paymentObjectName, 'paymentObjectName is missing');

	const { data, isPending } = usePayment(paymentObjectName);
	const { inwardDetails, outwardDetails } = useNestedPaymentForms(paymentObjectName);
	const formId = inwardDetails?.id ?? outwardDetails?.id;

	const { data: eventsData } = usePaymentEvents(paymentObjectName, formId);
	const { data: commentsData } = usePaymentComments(paymentObjectName);
	const { data: assessmentResult } = useTransactionAssessmentResults(paymentObjectName);

	const resendPaymentDecisionMutation = useResendPaymentDecisionMutation();

	const sortedAssessments = assessmentResult?.sort(
		(a, b) => new Date(b.meta['@createdAt']).getTime() - new Date(a.meta['@createdAt']).getTime()
	);

	const paymentStatus = data?.meta?.['@status'] ?? 'UNKNOWN';
	const decision = data?.data?.decision;
	const decisionWasMade = FINAL_STATUSES.includes(paymentStatus ?? '');
	const showResendButton = shouldShowResendButton(paymentStatus);
	const { data: cases } = usePaymentCases(data?.id ?? '');

	const handleResendDecision = async () => {
		if (!data?.id) return;

		const originalStatus = getOriginalStatusFromError(paymentStatus);
		if (!originalStatus) return;

		try {
			await resendPaymentDecisionMutation.mutateAsync({
				paymentId: data.id,
				originalStatus,
			});
			TFNotifier.success('Retrying payment decision submission');
		} catch (error) {
			TFNotifier.error('Failed to resend payment decision');
		}
	};

	if (isPending || !data || !overviewData || !eventsData) {
		return <TFLoadingOverlay />;
	}

	const paymentKind = data.kind;
	return (
		<>
			<TFPageContent>
				<TFPageHeader
					tenant={tenant}
					title={`${S.prettify(paymentKind)} payment ${M.format(data.labels.amount)} ${
						data.labels.amount_currency
					}`}
					breadcrumbs={[
						{ href: `/payments`, title: 'Payments' },
						{ href: `/payments/${paymentObjectName}`, title: data.labels.order_id },
					]}
					mb={0}
					sx={{ borderBottom: 'none' }}
					toolbar={
						<Button
							size="sm"
							leftSection={<Icon.IconArrowNarrowDown size={20} />}
							onClick={async () => await downloadEvidence(data.id)}
						>
							Download evidence
						</Button>
					}
				/>
				<PaymentToolbar
					orderId={data.labels.order_id}
					paymentId={data.id}
					status={paymentStatus}
					riskLevel={data.meta?.risk}
					assignedManagers={data.labels.assigned_managers ?? []}
					listUsers={listUsers}
					createdAt={data.meta['@createdAt']}
					updatedAt={data.meta['@updatedAt']}
				/>
				<Group mt="20px" gap="md" align="start" grow>
					<Stack>
						<TFCard title="Payment details">
							<PaymentDetails
								paymentId={data.id}
								decisionWasMade={decisionWasMade}
								paymentObjectName={paymentObjectName}
								paymentKind={paymentKind}
							/>
						</TFCard>

						{!decisionWasMade || cases?.length ? (
							<TFCard title="Cases">
								<PaymentCasesList decisionWasMade={decisionWasMade} paymentId={data.id} />
							</TFCard>
						) : null}
					</Stack>

					<Stack>
						{sortedAssessments && sortedAssessments.length > 0 ? (
							<TFCard title="Transaction risk assessment">
								<TransactionAssessmentResult
									status={paymentStatus}
									decision={decision}
									paymentId={data.id}
									assessmentResult={sortedAssessments}
								/>
							</TFCard>
						) : null}
						<TFCard title="Payment evidence log">
							<PaymentsEventLog
								listUsers={listUsers ?? []}
								events={eventsData?.sort(
									(a, b) =>
										new Date(b.meta['@createdAt']).getTime() - new Date(a.meta['@createdAt']).getTime()
								)}
							/>
						</TFCard>
						<TFCard>
							<Flex justify="flex-end" p="12px 16px">
								<Button
									variant="light"
									size="sm"
									leftSection={<Icon.IconMessageCircle size={16} />}
									onClick={commentsModal.open}
								>
									<Group gap=".25rem" sx={{ flexWrap: 'nowrap' }}>
										<TFText inherit>Comments</TFText>
										{commentsData?.length ? <CounterBadge value={commentsData.length} /> : null}
									</Group>
								</Button>
								{!decisionWasMade && (
									<Button ml="12px" size="sm" onClick={decisionModal.open}>
										Make decision
									</Button>
								)}
								{showResendButton && (
									<Button
										ml="12px"
										size="sm"
										variant="light"
										leftSection={<Icon.IconRefresh size={16} />}
										loading={resendPaymentDecisionMutation.isPending}
										onClick={handleResendDecision}
									>
										Resend decision
									</Button>
								)}
							</Flex>
						</TFCard>
					</Stack>
				</Group>
			</TFPageContent>
			<DecisionModal
				title="Payment decision"
				opened={decisionModal.isOpen}
				onClose={decisionModal.close}
				contentProps={{ paymentId: data.id, assessmentResult: sortedAssessments }}
				size="800"
				withinPortal
			/>
			<CommentsModal
				size="600"
				title="Comments"
				opened={commentsModal.isOpen}
				onClose={commentsModal.close}
				contentProps={{ paymentId: data.id, listUsers }}
				withinPortal
			/>
		</>
	);
};

interface DecisionModalProps {
	paymentId: string;
	assessmentResult?: TransactionAssessment[];
}

const DecisionModal = createModal(({ paymentId, assessmentResult }: DecisionModalProps) => {
	const { onClose } = useModalControl();
	const makePaymentDecisionMutation = useMakePaymentDecisionMutation();

	const { control, handleSubmit } = useForm<{ comment: string }>({
		defaultValues: { comment: '' },
	});

	const onSubmit = (data: { comment: string }) => {
		makePaymentDecisionMutation.mutate(
			{ paymentId, status: 'APPROVED', comment: data.comment },
			{ onSuccess: onClose }
		);
	};

	const onCancel = (data: { comment: string }) => {
		makePaymentDecisionMutation.mutate(
			{ paymentId, status: 'REJECTED', comment: data.comment },
			{ onSuccess: onClose }
		);
	};

	return (
		<Box>
			<Box p="12px">
				<Controller
					name="comment"
					control={control}
					rules={{ required: true }}
					render={({ field, fieldState }) => (
						<Textarea
							label="Please provide your comment"
							required
							{...field}
							minRows={4}
							maxRows={12}
							autosize
							error={Boolean(fieldState.error)}
						/>
					)}
				/>
			</Box>
			<Flex gap="12px" m="6px 12px 12px 12px">
				<Button color="red" variant="light" fullWidth onClick={handleSubmit(onCancel)}>
					Reject
				</Button>
				<Button fullWidth onClick={handleSubmit(onSubmit)}>
					Approve
				</Button>
			</Flex>
			{assessmentResult && assessmentResult.length > 0 && (
				<LastAssessmentResult assessmentResult={assessmentResult} />
			)}
		</Box>
	);
});

interface ModalProps {
	paymentId: string;
	listUsers: BackendTypes.UserInfo[];
}

export const CommentsModal = createModal(({ paymentId, listUsers }: ModalProps) => {
	const { data, isPending } = usePaymentComments(paymentId);
	const addPaymentCommentMutation = useAddPaymentCommentMutation();

	if (!data) {
		return <TFLoadingOverlay />;
	}

	const usersMap = listUsers.reduce<Record<string, string>>((acc, curr) => {
		acc[curr.userId] = getDisplayName(curr);
		return acc;
	}, {});

	const sortedComments = data
		?.sort((a, b) => new Date(a.meta['@createdAt']).getTime() - new Date(b.meta['@createdAt']).getTime())
		.map((c) => ({
			author: usersMap[c.meta?.['@author']?.['@id']] ?? 'Unknown user',
			createdAt: c.meta['@createdAt'],
			text: c.data?.comment,
		}));

	return (
		<CommentsList
			comments={sortedComments}
			sendComment={async (comment) =>
				addPaymentCommentMutation.mutate(
					{ paymentId, comment },
					{
						onError: () => {
							TFNotifier.error('Error while adding comment');
						},
					}
				)
			}
			isSubmitting={isPending}
		/>
	);
});
